import { Ban, CheckCircle2, CircleDot, Loader2 } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { cn } from "@/lib/utils";
import {
  COMMUNITY_ISSUE_CATEGORY_META,
  COMMUNITY_ISSUE_STATUS_META,
  type CommunityIssueCategory,
  type CommunityIssueStatus,
} from "@/lib/community-issues";

/**
 * 이슈 게시판 배지·아이콘·날짜 포맷 (JCG-3029). 훅이 없어 Server/Client 어디서나 사용 가능.
 * 색상은 `ui-patterns/dark-mode-colors.md` §1-2 팔레트 + light/dark 페어 규칙을 따른다.
 */

const STATUS_STYLES: Record<CommunityIssueStatus, { badge: string; icon: string }> = {
  open: {
    badge: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    icon: "text-green-600 dark:text-green-400",
  },
  in_progress: {
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
    icon: "text-amber-600 dark:text-amber-400",
  },
  resolved: {
    badge: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    icon: "text-purple-600 dark:text-purple-400",
  },
  closed: {
    badge: "bg-gray-200 text-gray-700 dark:bg-neutral-700 dark:text-neutral-200",
    icon: "text-gray-500 dark:text-neutral-400",
  },
};

const STATUS_ICONS: Record<
  CommunityIssueStatus,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  open: CircleDot,
  in_progress: Loader2,
  resolved: CheckCircle2,
  closed: Ban,
};

const CATEGORY_STYLES: Record<CommunityIssueCategory, string> = {
  bug: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  summary_pdf: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  question: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  suggestion: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
};

export function IssueStatusIcon({
  status,
  className,
}: {
  status: CommunityIssueStatus;
  className?: string;
}) {
  const Icon = STATUS_ICONS[status];
  return (
    <Icon
      className={cn("size-5 shrink-0", STATUS_STYLES[status].icon, className)}
      aria-label={COMMUNITY_ISSUE_STATUS_META[status].label}
      role="img"
    />
  );
}

export function IssueStatusBadge({
  status,
  className,
}: {
  status: CommunityIssueStatus;
  className?: string;
}) {
  const Icon = STATUS_ICONS[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
        STATUS_STYLES[status].badge,
        className
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {COMMUNITY_ISSUE_STATUS_META[status].label}
    </span>
  );
}

export function IssueCategoryBadge({
  category,
  className,
}: {
  category: CommunityIssueCategory;
  className?: string;
}) {
  const meta = COMMUNITY_ISSUE_CATEGORY_META[category];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        CATEGORY_STYLES[category],
        className
      )}
    >
      <span aria-hidden>{meta.emoji}</span>
      {meta.label}
    </span>
  );
}

/** 서버(UTC)·클라이언트 어디서 렌더돼도 같은 값이 나오도록 KST 를 명시한다. */
export function formatIssueDate(iso: string, withTime = false): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "long",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}
